import { useState } from 'react';
import { motion } from 'framer-motion';
import CRTEffect from 'vault66-crt-effect';
import 'vault66-crt-effect/dist/vault66-crt-effect.css';
import { NeonText } from '../effects';
import { Button, CVGenerator } from '../common';
import { useMouseParallax } from '@/hooks';
import { useLanguage } from '@/i18n';
import { FiDownload } from 'react-icons/fi';
import styles from './Hero.module.css';
import heroImage from '@/assets/fernando-hero.jpg';

export function Hero() {
  const [showCV, setShowCV] = useState(false);
  const { t } = useLanguage();
  const { parallax } = useMouseParallax({
    maxRotation: 25,
    smoothing: 0.05,
  });

  return (
    <section id="home" className={styles.hero}>
      <div className={styles.heroGrid}>
        {/* Left side - Name and content */}
        <div className={styles.content}>
          <motion.div
            className={styles.titleContainer}
            initial={{ opacity: 0, x: -50 }}
            animate={{
              opacity: 1,
              x: 0,
              rotateX: parallax.rotateX,
              rotateY: parallax.rotateY,
            }}
            transition={{ opacity: { duration: 0.8, delay: 0.2 }, x: { duration: 0.8, delay: 0.2 } }}
            style={{ perspective: 1000 }}
          >
            <div className={styles.nameGrid}>
              {/* FERNANDO - fila 1 */}
              <span className={styles.letter} data-letter="F">F</span>
              <span className={styles.letter} data-letter="E">E</span>
              <span className={styles.letter} data-letter="R">R</span>
              <span className={styles.letter} data-letter="N">N</span>
              <span className={styles.letter} data-letter="A">A</span>
              <span className={styles.letter} data-letter="N">N</span>
              <span className={styles.letter} data-letter="D">D</span>
              <span className={styles.letter} data-letter="O">O</span>
              {/* OLEA - fila 2, empieza en columna 8 para alinear O con O */}
              <span className={styles.letterOffset} data-letter="O">O</span>
              <span className={styles.letter} data-letter="L">L</span>
              <span className={styles.letter} data-letter="E">E</span>
              <span className={styles.letter} data-letter="A">A</span>
            </div>
          </motion.div>

          <motion.div
            className={styles.subtitleContainer}
            initial={{ opacity: 0, y: 30 }}
            animate={{
              opacity: 1,
              y: 0,
              rotateX: parallax.rotateX * 0.8,
              rotateY: parallax.rotateY * 0.8,
            }}
            transition={{ opacity: { duration: 0.8, delay: 0.5 }, y: { duration: 0.8, delay: 0.5 } }}
            style={{ perspective: 1000 }}
          >
            <NeonText as="h2" color="cyan" effect="glow" className={styles.subtitle} animate={false}>
              {t.hero.subtitle.split('\n').map((line, i, arr) => (
                <span key={i}>
                  {line}
                  {i < arr.length - 1 && <br />}
                </span>
              ))}
            </NeonText>
          </motion.div>

          <motion.p
            className={styles.tagline}
            initial={{ opacity: 0 }}
            animate={{
              opacity: 1,
              rotateX: parallax.rotateX * 0.6,
              rotateY: parallax.rotateY * 0.6,
            }}
            transition={{ opacity: { duration: 0.8, delay: 0.8 } }}
            style={{ perspective: 1000 }}
          >
            {t.hero.tagline} <span className={styles.highlight}>XR</span>,{' '}
            <span className={styles.highlight}>IA</span> &{' '}
            <span className={styles.highlight}>3D Configurators</span> · +
            <span className={styles.highlight}>9</span> {t.hero.yearsExperience}
          </motion.p>

          <motion.div
            className={styles.cta}
            initial={{ opacity: 0, y: 20 }}
            animate={{
              opacity: 1,
              y: 0,
              rotateX: parallax.rotateX * 0.7,
              rotateY: parallax.rotateY * 0.7,
            }}
            transition={{ opacity: { duration: 0.6, delay: 1.1 }, y: { duration: 0.6, delay: 1.1 } }}
            style={{ perspective: 1000 }}
          >
            <Button href="#contact" variant="primary" size="lg">
              {t.hero.contactMe}
            </Button>
            <Button href="#portfolio" variant="outline" size="lg">
              {t.hero.viewProjects}
            </Button>
            <button className={styles.cvButton} onClick={() => setShowCV(true)}>
              <FiDownload className={styles.cvIcon} />
              {t.hero.downloadCV}
            </button>
          </motion.div>

          <motion.div
            className={styles.contactInfo}
            initial={{ opacity: 0 }}
            animate={{
              opacity: 1,
              rotateX: parallax.rotateX * 0.5,
              rotateY: parallax.rotateY * 0.5,
            }}
            transition={{ opacity: { duration: 0.6, delay: 1.4 } }}
            style={{ perspective: 1000 }}
          >
            <a href="mailto:contact@fernandoolea.com" className={styles.contactLink}>
              contact@fernandoolea.com
            </a>
            <span className={styles.separator}>|</span>
            <a href="tel:+34636276078" className={styles.contactLink}>
              +34 636 27 60 78
            </a>
          </motion.div>
        </div>

        {/* Right side - Photo with glitch frame */}
        <motion.div
          className={styles.photoContainer}
          initial={{ opacity: 0, x: 50 }}
          animate={{ opacity: 1, x: 0 }}
          transition={{ duration: 0.8, delay: 0.4 }}
        >
          <motion.div
            className={styles.glitchFrame}
            animate={{
              rotateX: parallax.rotateX,
              rotateY: parallax.rotateY,
            }}
            style={{ perspective: 1000 }}
          >
            <div className={styles.frameCorner} data-corner="top-left" />
            <div className={styles.frameCorner} data-corner="top-right" />
            <div className={styles.frameCorner} data-corner="bottom-left" />
            <div className={styles.frameCorner} data-corner="bottom-right" />
            <div className={styles.frameLine} data-line="top" />
            <div className={styles.frameLine} data-line="right" />
            <div className={styles.frameLine} data-line="bottom" />
            <div className={styles.frameLine} data-line="left" />
            <div className={styles.photoWrapper}>
              <CRTEffect
                preset="minimal"
                theme="blue"
                enableScanlines={true}
                enableSweep={true}
                enableGlow={false}
                enableFlicker={false}
                sweepDuration={12}
                glitchSpeed={0.1}
              >
                <img
                  src={heroImage}
                  alt="Fernando Olea"
                  className={styles.heroPhoto}
                />
              </CRTEffect>
            </div>
          </motion.div>
        </motion.div>
      </div>

      {/* Scroll indicator */}
      <motion.div
        className={styles.scrollIndicator}
        initial={{ opacity: 0 }}
        animate={{ opacity: 1, y: [0, 10, 0] }}
        transition={{
          opacity: { delay: 2, duration: 0.5 },
          y: { delay: 2, duration: 1.5, repeat: Infinity },
        }}
      >
        <span className={styles.scrollText}>{t.hero.scroll}</span>
        <div className={styles.scrollLine} />
      </motion.div>

      {/* CV Generator Modal */}
      {showCV && <CVGenerator onClose={() => setShowCV(false)} />}
    </section>
  );
}
